import { css } from '@/styled-system/css';

/**
 * The dialog owns its own markup (HP-8998), so these style the real thing
 * rather than overriding Constellation's dropdown internals. Nothing here is
 * keyed on a `data-c11n-component` attribute, so a Constellation bump can't
 * silently revert it.
 */

// The close button now sits in the field, so the header's end padding matches its start.
export const header = css.raw({
  paddingBlock: 'layout.tight',
  paddingInlineStart: 'layout.tight',
  paddingInlineEnd: 'layout.tight',
});

// Modal.CloseButton is absolute by default; in the field it is a flex item.
export const closeButton = css.raw({
  position: 'relative',
  insetBlockStart: 'auto',
  insetInlineEnd: 'auto',
  zIndex: 'auto',
});

// Centres the 40px button in the 48px field with the same 4px on its end, which c11n pads to 0.
export const closeAdornment = css.raw({ paddingBlock: 'zero', alignSelf: 'center', marginInlineEnd: 'tightest' });

// The rows carry their own inset and paint their hover edge to edge, so the body has no padding.
export const body = css.raw({
  paddingBlock: 'zero',
  paddingInline: 'zero',
  minBlockSize: 'zero',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
});

// Names the dialog; see `aria-labelledby` in the component.
export const srOnly = css({ srOnly: true });

/*
 * HP-9107. Groups are placed by transform, not by flow, because CLS does not bill
 * movement caused by a transform change. Consecutive autocomplete responses re-compose
 * the list — "denver" -> "denver c" takes Cities 10/Properties 5 to Cities
 * 2/Neighborhoods 8/Properties 5 — and in flow every group below a changed row count
 * genuinely moves, which the field billed at 0.71 once the response landed outside the
 * 500ms input grace window.
 *
 * A transform offset has to be computable without measuring, so the heading and row
 * boxes below are fixed rather than intrinsic. That is the trade this buys: a label too
 * long for one line now ellipsises instead of wrapping to a second.
 *
 * The component supplies `--list-groups`/`--list-rows` here and `--groups-before`/
 * `--rows-before` per group, all unitless counts.
 *
 * The two box sizes are named tokens — `sizes.locationSuggestion.heading` and `.row`,
 * defined in `panda.config.ts` — so the boxes below and the offsets above cannot drift
 * apart: all four references resolve to the same value. They are not hoisted into CSS
 * custom properties here because `token.var()` would pull the runtime token map into
 * this bundle (`constellation/no-runtime-token`).
 */
export const listbox = css({
  position: 'relative',
  blockSize:
    'calc(var(--list-groups) * {sizes.locationSuggestion.heading} + var(--list-rows) * {sizes.locationSuggestion.row})',
});

export const suggestionGroup = css({
  position: 'absolute',
  insetBlockStart: 'zero',
  insetInlineStart: 'zero',
  insetInlineEnd: 'zero',
  transform:
    'translateY(calc(var(--groups-before) * {sizes.locationSuggestion.heading} + var(--rows-before) * {sizes.locationSuggestion.row}))',
});

// Rows and headings deliberately paint no background of their own: the dialog's
// own sheet shows through, so there's no seam where the list ends. Painting
// `bg.neutral` here left a visible two-tone band below the last row in dark
// mode, where the sheet and bg.neutral are different shades.
export const groupHeading = css({
  textStyle: 'body-sm-bold',
  color: 'text.subtle',
  textTransform: 'uppercase',
  paddingInline: 'default',
  // Fixed, not padding-derived: the group transform above is computed from counts.
  blockSize: 'locationSuggestion.heading',
  // Same reason as the row label: the box no longer grows, so a heading long enough to
  // wrap would spill out of it rather than push the group taller — measured overflowing
  // at 120 characters, which a locale with longer category names could reach. A block
  // with its line box set to the full height centres the single line without flex, which
  // `text-overflow` would not survive: the text would become an anonymous flex item and
  // the ellipsis would never apply.
  lineHeight: '{sizes.locationSuggestion.heading}',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

// Icon spans both text rows; label over meta in the second column.
export const suggestionRow = css({
  display: 'grid',
  // `minmax(0, 1fr)`, not `1fr`: a grid track's default `min-width: auto` floors at the
  // content's intrinsic width, so the label below would never ellipsise.
  gridTemplateColumns: 'auto minmax(0, 1fr)',
  gridTemplateRows: 'auto auto',
  alignItems: 'center',
  // The box is taller than its content now, and the icon spans both text rows. Without
  // this the two rows sit at the top while the icon centres against the whole box, so a
  // row with no meta line shows its label above its own pin.
  alignContent: 'center',
  columnGap: 'tighter',
  width: '100%',
  // Fixed rather than a floor, so the group transform is computable from row counts.
  blockSize: 'locationSuggestion.row',
  paddingInline: 'default',
  textAlign: 'start',
  cursor: 'pointer',
  // Dividers separate rows, so the last one in a group doesn't draw one — it
  // would otherwise hang under the list with empty sheet below it. Between
  // groups the next heading provides the separation.
  borderBottomWidth: 'default',
  borderBottomStyle: 'solid',
  borderBottomColor: 'border.neutral',
  _last: { borderBottomWidth: 'zero' },
  transition: {
    _motionReduce: 'none',
    _motionSafe: 'background-color 200ms ease',
  },
  _hover: { backgroundColor: 'bg.action.neutral.alt.hover' },
  // Deliberately not `bg.action.neutral.alt.pressed`, which is what c11n's own
  // Combobox.Option pairs with the hover token above: the two resolve to the
  // same colour in this theme, so pressing an already-hovered row painted
  // nothing. This is the next step along the same teal ramp in both themes
  // (teal.100 -> teal.300 light, teal.700 -> teal.600 dark), so the press stays
  // on the row's own side of the lightness scale and the label keeps its
  // contrast — the `impact` ramp inverts for on-impact surfaces and would flash
  // a slab the text can't be read against. Instant on the way in, faded on the
  // way out: a 200ms ease-in never lands before a quick tap releases.
  _active: { backgroundColor: 'bg.action.soft.selected.pressed', transitionDuration: '0ms' },
  // Highlight follows `aria-activedescendant`, so keyboard and pointer users see
  // the same affordance. NOT `data-active`: Panda compiles its own `_active`
  // condition to `&:is(:active, [data-active])`, which matches the attribute
  // whatever its value, so `data-active="false"` would paint every row at once.
  '&[data-highlighted]': { backgroundColor: 'bg.action.neutral.alt.hover' },
});

export const rowIcon = css({
  gridRow: '1 / span 2',
  display: 'flex',
  alignItems: 'center',
  color: 'icon.action.neutral.default',
});

// Both truncate rather than wrap: the row box is fixed, so a second line would overflow
// it rather than grow it.
export const rowLabel = css({
  textStyle: 'body',
  color: 'text.neutral',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const rowMeta = css({
  textStyle: 'body-sm',
  color: 'text.subtle',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export const statusRow = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'tighter',
  paddingBlock: 'loose',
  paddingInline: 'default',
  color: 'text.subtle',
  textStyle: 'body',
});

export const emptyState = css({
  paddingBlock: 'loose',
  paddingInline: 'default',
  color: 'text.subtle',
  textStyle: 'body',
});
